import type { TokenEntry } from '@/lib/designTokenParser';
import { updateTokenInMarkdown } from '@/lib/designTokenParser';

interface TypographyTokenGroupProps {
  title: string;
  tokens: TokenEntry[];
  markdown: string;
  onMarkdownChange: (md: string) => void;
  /** Controls how the preview renders: fontFamily, fontSize, or fontWeight */
  previewType: 'fontFamily' | 'fontSize' | 'fontWeight';
}

const cardStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  backgroundColor: 'var(--color-card-surface)',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  padding: '2px 6px',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  fontSize: '11px',
  fontFamily: 'monospace',
  boxSizing: 'border-box',
  width: '100%',
};

export function TypographyTokenGroup({ title, tokens, markdown, onMarkdownChange, previewType }: TypographyTokenGroupProps) {
  if (tokens.length === 0) return null;

  return (
    <div style={{ marginBottom: '20px' }}>
      <h4 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px', marginTop: 0 }}>
        {title}
      </h4>
      {tokens.map((token) => (
        <div key={`${token.lineIndex}-${token.name}`} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                {token.name}
              </div>
              <input
                style={inputStyle}
                value={token.value}
                onChange={(e) => {
                  onMarkdownChange(updateTokenInMarkdown(token.lineIndex, e.target.value, markdown));
                }}
              />
              {token.comment && (
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{token.comment}</div>
              )}
            </div>
            <div style={{
              flex: 1,
              padding: '8px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '6px',
              overflow: 'hidden',
            }}>
              <TypographyPreview value={token.value} type={previewType} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TypographyPreview({ value, type }: { value: string; type: 'fontFamily' | 'fontSize' | 'fontWeight' }) {
  const sampleText = type === 'fontWeight' ? 'Design System' : type === 'fontSize' ? 'Aa' : 'The quick brown fox';

  const style: React.CSSProperties = {
    color: 'var(--text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  if (type === 'fontFamily') {
    // Strip quotes for CSS but keep original value
    style.fontFamily = value;
    style.fontSize = '14px';
  } else if (type === 'fontSize') {
    style.fontSize = value;
    style.lineHeight = 1.2;
  } else if (type === 'fontWeight') {
    style.fontWeight = value;
    style.fontSize = '14px';
  }

  return <div style={style}>{sampleText}</div>;
}
